import attendeesTemplate from './attendees.template.html';

/*******************************************************************************
 * attendees */
/**
 * @ngdoc     component
 * @module    csm.books.eventsAdmin
 * @name      csm.books.eventsAdmin.component:attendees
 * @description
 * This component handles the "Attendees" section content in the
 * AddChangeEventDialog component. It displays attendees, allows for uploading a
 * list of attendees, adding and removing individual attendees and guests.
 *******************************************************************************/ 
export const attendeesComponent = {
  require: { dialogCtrl: '^^addChangeEventDialog' },
  template: attendeesTemplate,
  controller: Controller
}

/*******************************************************************************
 * Controller
 *******************************************************************************/
/* @ngInject */ function Controller($scope, $injector) {
  var $ctrl = this;
  $ctrl.addAttendee = $injector.invoke(addAttendee, null, { $ctrl: $ctrl });
  $ctrl.addGuest = $injector.invoke(addGuest, null, { $ctrl: $ctrl });
  $ctrl.dgAttendees = $injector.invoke(buildGridOptions, null, {
    $ctrl: $ctrl
  });
  $ctrl.loadAttendees = $injector.invoke(LoadAttendees, null, {
    $ctrl: $ctrl,
    $scope: $scope
  });
  $ctrl.$onInit = $injector.invoke($onInit, null, { $ctrl: $ctrl });
  $ctrl.removeAttendee = $injector.invoke(removeAttendee, null, {
    $ctrl: $ctrl
  });
  $ctrl.uploadAttendeeList = $injector.invoke(uploadAttendeeList, null, {
    $ctrl: $ctrl
  });
  $ctrl.validateAttendeeID = $injector.invoke(validateAttendeeID, null, {
    $ctrl: $ctrl
  });
}

/*******************************************************************************
 * Controller.addAttendee */
/**
 * @ngdoc     function
 * @name      addAttendee
 * @module    csm.books.eventsAdmin
 * @methodOf  csm.books.eventsAdmin.controller:attendees
 * @description
 * Handles adding a single attendee with an optional guest to the event.
 *******************************************************************************/
/* @ngInject */ function addAttendee($ctrl, Alert, BusyScreen, Events) {
  return function () {
    /*-----------------------------------------------------*/
    /* Attendees can not be manually added to open events. */
    /*-----------------------------------------------------*/
    if ($ctrl.dialogCtrl.event.openEvent) return;

    /*-----------------------------------------*/
    /* Can not add attendee to unsaved events. */
    /*-----------------------------------------*/
    if ($ctrl.dialogCtrl.isNew) {
      Alert.error('Event must be saved before an attendee can be added');
      return;
    }

    /*------------------------*/
    /* Check required fields. */
    /*------------------------*/
    if (!$ctrl.validator.validate()) return;

    /*-----------------------------------------*/
    /* Get the ID of the event from the parent */
    /* dialog and save on the new attendee.    */
    /*-----------------------------------------*/
    $ctrl.attendee.eventID = $ctrl.dialogCtrl.event.id;

    /*--------------------*/
    /* Save the attendee. */
    /*--------------------*/
    BusyScreen.setBusy(true);
    Events.saveAttendee($ctrl.attendee)
      .then(
        function (resp) {
          /*---------------------------------------------------------*/
          /* Update attendee with ID and add to the top of the grid. */
          /*---------------------------------------------------------*/
          $ctrl.attendee.id = resp;
          $ctrl.dgAttendees.data.unshift($ctrl.attendee);

          /*---------------------------------------------------------------*/
          /* Alert the user, clear the Add Attendee inputs and place focus */
          /* on the Last Name field to allow for another attendee add.     */
          /*---------------------------------------------------------------*/
          Alert.success('Attendee added.');
          $ctrl.attendee = { studentID: '' };
          document.getElementById('attendee-lastname').focus();
        },
        /*----------------------*/
        /* Handle server error. */
        /*----------------------*/
        function (resp) {
          Alert.error(resp.parsedResponse.message(), 'Save Error');
        }
      )
      .finally(function () {
        BusyScreen.setBusy(false);
      });
  };
}

/*******************************************************************************
 * Controller.addGuest */
/**
 * @ngdoc     function
 * @name      addGuest
 * @module    csm.books.eventsAdmin
 * @methodOf  csm.books.eventsAdmin.controller:attendees
 * @description
 * Adds a guest to the provided attendee.
 *******************************************************************************/
/* @ngInject */ function addGuest(
  $ctrl,
  AddGuestDialog,
  Alert,
  BusyScreen,
  Events
) {
  return function (attendee) {
    /*---------------------------------------------------*/
    /* When in read only mode or if event is open, exit. */
    /*---------------------------------------------------*/
    if ($ctrl.dialogCtrl.readOnlyMode || $ctrl.dialogCtrl.event.openEvent)
      return;

    /*--------------------------*/
    /* Nothing to remove, exit. */
    /*--------------------------*/
    if (!attendee || !attendee.id) return;

    /*------------------------------*/
    /* Display dialog to add guest. */
    /*------------------------------*/
    AddGuestDialog.show(attendee, $ctrl.dialogCtrl.event.id).then(
      function () {
        $ctrl.loadAttendees();
      }
    );
  };
}

/*******************************************************************************
 * Controller.buildGridOptions */
/**
 * @ngdoc     function
 * @name      buildGridOptions
 * @module    csm.books.eventsAdmin
 * @methodOf  csm.books.eventsAdmin.controller:attendees
 * @returns  {object}  uiGrid definition object
 * @description
 * Generates the uiGrid definition object to be used with the attendees grid.
 *******************************************************************************/
/* @ngInject */ function buildGridOptions(
  $ctrl,
  CommonUIGridTemplates,
  Tools,
  uiGridConstants
) {
  /*----------------------------------------------------------------*/
  /* Utility function that returns the css class string "from-sale" */
  /* to display cell contents for * attendees from sale in italics. */
  /*----------------------------------------------------------------*/
  function fromSale(grid, row) {
    return !Tools.isNaNZero(row.entity.ledgerItemID) ? 'from-sale' : '';
  }

  /*------------------------------------------------------------------*/
  /* Utility function to return the css class "hide-delete" to hide   */
  /* the delete button. For attendees that have been added from sale. */
  /*------------------------------------------------------------------*/
  function manageDelete(grid, row) {
    return !Tools.isNaNZero(row.entity.ledgerItemID) ? 'hide-delete' : '';
  }

  /*-------------------------------------------------*/
  /* Template for the actions column with icons to   */
  /* manage attendees, reset, edit and delete event. */
  /*-------------------------------------------------*/
  var actionsCellTemplate =
    '<div class="ui-grid-cell-contents ng-binding ng-scope"> ' +
    '<span class="glyphicon glyphicon-plus  csm-grid-space-target pointer" title="Add/Change Guest" ng-click="grid.appScope.$ctrl.addGuest(row.entity)"></span> &nbsp;' +
    '<span class="glyphicon glyphicon-trash csm-grid-space-target pointer" title="Remove Attendee"  ng-click="grid.appScope.$ctrl.removeAttendee(row.entity)"></span> &nbsp;' +
    '</div>';
  return {
    enableColumnMenus: false,
    enableFiltering: true,
    minRowsToShow: 5,
    enableHorizontalScrollbar: uiGridConstants.scrollbars.NEVER,
    enableVerticalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
    data: [],
    columnDefs: [
      {
        cellClass: fromSale,
        displayName: 'Student ID',
        field: 'studentID',
        type: 'number'
      },
      {
        cellClass: fromSale,
        field: 'lastName'
      },
      {
        cellClass: fromSale,
        field: 'firstName'
      },
      {
        cellClass: fromSale,
        displayName: 'Guest',
        field: 'guestName'
      },
      {
        cellTemplate: actionsCellTemplate,
        enableFiltering: false,
        enableSorting: false,
        field: 'actions',
        width: 90,
        displayName: 'Actions',
        pinnedRight: true
      }
    ]
  };
}

/*******************************************************************************
 * Controller.loadAttendees */
/**
 * Loads the attendees and sets the list to the datagrid.
 *******************************************************************************/
/* @ngInject */ function LoadAttendees($ctrl, $scope, BusyScreen, Events) {
  return function () {
    return BusyScreen.promise(
      Events.getAttendees($ctrl.dialogCtrl.event.id).then(function (data) {
        $ctrl.dgAttendees.data = data;
      })
    );
  };
}

/*******************************************************************************
 * Controller.$onInit */
/**
 * @ngdoc     function
 * @name      $onInit
 * @module    csm.books.eventsAdmin
 * @methodof  csm.books.eventsAdmin.controller:attendees
 * @description
 * Called by angular when the component is ready. This will set up some
 * properties from the bound values.
 *******************************************************************************/
/* @ngInject */ function $onInit($ctrl, CommonUIGridTemplates, UserAgent) {
  return function () {
    /** Validator object. */ $ctrl.validator = {};
    /** New Attendee object. */ $ctrl.attendee = { studentID: '' };

    /*-----------------------------------------------*/
    /* Populate the list of attendees from           */
    /* dataprovider on the parent dialog controller. */
    /*-----------------------------------------------*/
    $ctrl.dgAttendees.data = $ctrl.dialogCtrl.dpAttendees;

    /*---------------------------------------*/
    /* Hide the actions column in read only. */
    /*---------------------------------------*/
    $ctrl.dgAttendees.columnDefs[4].visible = !$ctrl.dialogCtrl.readOnlyMode;
  };
}

/*******************************************************************************
 * Controller.removeAttendee */
/**
 * @ngdoc     function
 * @name      removeAttendee
 * @module    csm.books.eventsAdmin
 * @methodOf  csm.books.eventsAdmin.controller:attendees
 * @param  attendee  EventAttendee to remove
 * Handles the request to remove an attendee from the current list.
 *******************************************************************************/
/* @ngInject */ function removeAttendee(
  $ctrl,
  Alert,
  BusyScreen,
  Events,
  Tools
) {
  return function (attendee) {
    /*------------------------------------------*/
    /* Prevent modifications in read only mode. */
    /*------------------------------------------*/
    if ($ctrl.dialogCtrl.readOnlyMode) return;

    /*--------------------------*/
    /* Nothing to remove, exit. */
    /*--------------------------*/
    if (!attendee || !attendee.id) return;

    /*-------------------------------------*/
    /* Do not delete attendees from sales. */
    /*-------------------------------------*/
    if (!Tools.isNaNZero(attendee.ledgerItemID)) {
      Alert.error(
        'This attendee is from a sale and cannot be removed.',
        'Delete Error'
      );
      return;
    }

    /*----------------------------------------*/
    /* Confirm and call svc to drop attendee. */
    /*----------------------------------------*/
    Alert.confirm(
      'Are you sure you want to remove this person from the event?',
      'Confirm Removal'
    ).then(function () {
      BusyScreen.setBusy(true);
      Events.dropAttendee(attendee.id, $ctrl.dialogCtrl.event.id).then(
        function (resp) {
          /*--------------------------------*/
          /* Remove attendee from the grid. */
          /*--------------------------------*/
          $ctrl.dgAttendees.data.removeItem(attendee);
          BusyScreen.setBusy(false);
          Alert.success('Attendee removed.');
        }
      );
    });
  };
}

/*******************************************************************************
 * Controller.uploadAttendeeList */
/**
 * @ngdoc     method
 * @name      uploadAttendeeList
 * @module    csm.books.eventsAdmin
 * @methodof  csm.books.eventsAdmin.controller:attendees
 * @description
 * Display the dialog to upload a list of attendees. On successful upload,
 * reload the list and update the grid.
 *******************************************************************************/
/* @ngInject */ function uploadAttendeeList(
  $ctrl,
  Alert,
  BusyScreen,
  Events,
  UploadAttendeesDialog
) {
  /** Dialog active flag. */ var dialogActive = false;

  return function () {
    /*------------------------------------------*/
    /* Prevent modifications in read only mode. */
    /*------------------------------------------*/
    if ($ctrl.dialogCtrl.readOnlyMode) return;

    /*---------------------------------------*/
    /* New event, prompt user to save first. */
    /*---------------------------------------*/
    if ($ctrl.dialogCtrl.isNew) {
      Alert.confirm(
        'Event must be saved before attendees can be uploaded. Do you want to save?',
        'New Event'
      ).then(function () {
        $ctrl.dialogCtrl.saveEvent(true);
      });
    } else {
      /*---------------------------------*/
      /* Dialog is already active, exit. */
      /*---------------------------------*/
      if (dialogActive) return;

      /*-------------------------------------------------*/
      /* Confirm and display dialog to upload attendees. */
      /*-------------------------------------------------*/
      Alert.confirm(
        'Are you sure you wish to overwrite the attendees list?',
        'Confirm Overwrite'
      ).then(function () {
        UploadAttendeesDialog.show($ctrl.dialogCtrl.event)
          .then(function () {
            dialogActive = true;
            BusyScreen.promise(
              Events.getAttendees($ctrl.dialogCtrl.event.id).then(
                function (attendees) {
                  $ctrl.dgAttendees.data = attendees;
                }
              )
            );
          })
          .finally(function () {
            dialogActive = false;
          });
      });
    }
  };
}

/*******************************************************************************
 * Controller.validateAttendeeID */
/**
 * @ngdoc     method
 * @name      validateAttendeeID
 * @module    csm.books.eventsAdmin
 * @methodof  csm.books.eventsAdmin.controller:attendees
 * @description
 * Custom validator for the Student ID input for Add Attendee. Ensures the
 * studentID is not already in the current list of attendees.
 *******************************************************************************/
/* @ngInject */ function validateAttendeeID($ctrl, Tools) {
  return function () {
    /*------------------------------------------------------*/
    /* Student ID is empty, which is ok. Validation passed. */
    /*------------------------------------------------------*/
    if (Tools.isEmpty($ctrl.attendee.studentID)) return null;
    /*----------------------------------*/
    /* Student ID provided, check if it */
    /* exists in the list of attendees. */
    /*----------------------------------*/ else if (
      $ctrl.dgAttendees.data.some(function (at) {
        return at.studentID == $ctrl.attendee.studentID;
      })
    )
      return 'This student ID is already associated with an attendee';
    /*---------------------------------------------------*/
    /* Student ID is not a duplicate, validation passed. */
    /*---------------------------------------------------*/ else return null;
  };
}
